/**
 * Bidoryx Theme System
 * Supports: Dark, Light, and Auto modes
 */

export const themes = {
  dark: {
    name: 'Dark',
    mode: 'dark',
    colors: {
      // Backgrounds
      bg: {
        primary: '#0f0f1e',        // Main background
        secondary: '#1a1a2e',      // Cards, panels
        tertiary: '#16213e',       // Nested elements
        hover: 'rgba(255, 255, 255, 0.05)',
        elevated: '#1a1a2e'
      },
      
      // Text
      text: {
        primary: '#ffffff',
        secondary: 'rgba(255, 255, 255, 0.7)',
        tertiary: 'rgba(255, 255, 255, 0.5)',
        muted: 'rgba(255, 255, 255, 0.3)'
      },
      
      // Borders
      border: {
        primary: 'rgba(255, 255, 255, 0.1)',
        secondary: 'rgba(255, 255, 255, 0.05)',
        focus: '#8b5cf6'
      },
      
      // Brand
      brand: {
        primary: '#8b5cf6',
        secondary: '#6366f1',
        gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
      },
      
      // Status
      status: {
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
        info: '#3b82f6'
      },
      
      // Semantic
      positive: '#10b981',
      negative: '#ef4444',
      neutral: '#6b7280'
    }
  },
  
  light: {
    name: 'Light',
    mode: 'light',
    colors: {
      // Backgrounds - MAXIMUM contrast for readability
      bg: {
        primary: '#f5f5f7',        // Apple-style light gray
        secondary: '#ffffff',      // Pure white cards
        tertiary: '#e8e8ed',       // Slightly darker for nested
        hover: 'rgba(99, 102, 241, 0.12)',  // More visible purple hover
        elevated: '#ffffff'
      },
      
      // Text - VERY dark for maximum readability
      text: {
        primary: '#000000',        // Pure black for headings
        secondary: '#1d1d1f',      // Almost black for body text
        tertiary: '#6e6e73',       // Medium gray (Apple style)
        muted: '#86868b'          // Light gray for hints
      },
      
      // Borders - clearly visible
      border: {
        primary: '#d2d2d7',        // Visible but not harsh
        secondary: '#e5e5ea',      // Very subtle
        focus: '#8b5cf6'
      },
      
      // Brand
      brand: {
        primary: '#8b5cf6',
        secondary: '#6366f1',
        gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
      },
      
      // Status
      status: {
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
        info: '#3b82f6'
      },
      
      // Semantic
      positive: '#10b981',
      negative: '#ef4444',
      neutral: '#6b7280'
    }
  }
};

/**
 * Get theme based on user preference or auto mode
 * @param {string} preference - 'dark', 'light', or 'auto'
 * @returns {object} - Theme object
 */
export function getTheme(preference = 'dark') {
  if (preference === 'auto') {
    const hour = new Date().getHours();
    // Dark mode: 6PM - 8AM (18:00 - 08:00)
    // Light mode: 8AM - 6PM (08:00 - 18:00)
    return (hour >= 18 || hour < 8) ? themes.dark : themes.light;
  }
  
  return themes[preference] || themes.dark;
}

/**
 * Save theme preference to localStorage
 */
export function saveThemePreference(preference) {
  localStorage.setItem('bidoryx-theme', preference);
}

/**
 * Load theme preference from localStorage
 */
export function loadThemePreference() {
  return localStorage.getItem('bidoryx-theme') || 'dark';
}

/**
 * Apply theme to document root
 */
export function applyThemeToDocument(theme) {
  const root = document.documentElement;
  
  // Apply CSS variables
  root.style.setProperty('--bg-primary', theme.colors.bg.primary);
  root.style.setProperty('--bg-secondary', theme.colors.bg.secondary);
  root.style.setProperty('--bg-tertiary', theme.colors.bg.tertiary);
  root.style.setProperty('--bg-hover', theme.colors.bg.hover);
  
  root.style.setProperty('--text-primary', theme.colors.text.primary);
  root.style.setProperty('--text-secondary', theme.colors.text.secondary);
  root.style.setProperty('--text-tertiary', theme.colors.text.tertiary);
  root.style.setProperty('--text-muted', theme.colors.text.muted);
  
  root.style.setProperty('--border-primary', theme.colors.border.primary);
  root.style.setProperty('--border-secondary', theme.colors.border.secondary);
  
  root.style.setProperty('--brand-primary', theme.colors.brand.primary);
  
  // Set data attribute for theme mode
  root.setAttribute('data-theme', theme.mode);
}

